import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Ticket, Calendar, MapPin, QrCode, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import QRCode from 'qrcode';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyTickets();
  }, []);

  const fetchMyTickets = async () => {
    try {
      const response = await axios.get(`${API_URL}/tickets/my-tickets`);
      setTickets(response.data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Errore nel caricamento dei biglietti');
    } finally {
      setLoading(false);
    }
  };

  const downloadTicketQR = async (ticket) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(ticket.qr_code, {
        width: 300,
        margin: 2
      });
      
      const link = document.createElement('a');
      link.href = qrDataUrl;
      link.download = `ticket-${ticket.id}.png`;
      link.click();
      
      toast.success('QR Code scaricato');
    } catch (error) {
      toast.error('Errore nel download del QR Code');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Logo size="xl" linkTo={null} className="animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Logo size="md" linkTo="/launcher" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">I Miei Biglietti</h1>
              <p className="text-sm text-gray-600">Tutti i tuoi biglietti acquistati</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tickets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Ticket className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nessun Biglietto</h3>
              <p className="text-gray-600">Non hai ancora acquistato biglietti.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-purple-500 to-blue-500 text-white">
                  <CardTitle className="flex items-center gap-2">
                    <Ticket className="w-5 h-5" />
                    {ticket.event_title || 'Evento'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Info */}
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Data</p>
                          <p className="font-medium">
                            {ticket.event_date ? new Date(ticket.event_date).toLocaleDateString('it-IT') : 'Data non disponibile'}
                          </p>
                        </div>
                      </div>
                      {ticket.event_location && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Luogo</p>
                            <p className="font-medium">{ticket.event_location}</p>
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-600">Tipo Biglietto</p>
                        <p className="font-medium">{ticket.ticket_type_name || 'Standard'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Stato</p>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          ticket.is_used ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {ticket.is_used ? 'Utilizzato' : 'Valido'}
                        </span>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="flex flex-col items-center justify-center">
                      <div className="bg-white p-4 rounded-lg border-2 border-gray-200 mb-3">
                        <div id={`qr-${ticket.id}`}>
                          <QRCodeDisplay qrCode={ticket.qr_code} />
                        </div>
                      </div>
                      <button
                        onClick={() => downloadTicketQR(ticket)}
                        className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Scarica QR Code
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QRCodeDisplay({ qrCode }) {
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    if (qrCode) {
      QRCode.toDataURL(qrCode, { width: 200, margin: 1 })
        .then(url => setQrDataUrl(url))
        .catch(err => console.error('Error generating QR:', err));
    }
  }, [qrCode]);

  return qrDataUrl ? (
    <img src={qrDataUrl} alt="QR Code" className="w-48 h-48" />
  ) : (
    <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
      <QrCode className="w-12 h-12 text-gray-400" />
    </div>
  );
}
